import CopyOutlined from '@ant-design/icons/CopyOutlined'
import RobotOutlined from '@ant-design/icons/RobotOutlined'
import UserOutlined from '@ant-design/icons/UserOutlined'
import { Avatar, Button, Card, Typography } from 'antd'
import { EXPLORER_HIGHLIGHT_COLOR } from 'features/drilldown'
import { MessageBubbleProps } from 'features/drilldown/panels/global/llmPanel/types'
import React, { FC } from 'react'
import ReactMarkdown from 'react-markdown'
// (xdowns) Enable once we can install react-syntax-highlighter
// import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
// import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const { Text } = Typography

// Minimal styled components
const MessageRow = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.$isUser ? 'row-reverse' : 'row')};
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`

const MessageContent = styled.div<{ $isUser: boolean }>`
  max-width: 70%;
  align-self: ${(props) => (props.$isUser ? 'flex-end' : 'flex-start')};
`

const MessageCard = styled(Card)<{ $isUser: boolean }>`
  background-color: ${(props) =>
    props.$isUser ? '#1890ff' : CantileverTheme.colors.grayscale['200']};
  border-radius: ${(props) =>
    props.$isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px'};

  .ant-card-body {
    padding: 12px 16px;
  }
`

const CopyButton = styled(Button)`
  opacity: 0.7;
  &:hover {
    opacity: 1;
  }
`

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`

export const LlmMessageText = styled.div<{ $isUser: boolean }>`
  line-height: 1.5;
  color: ${(props) =>
    props.$isUser ? 'white' : CantileverTheme.colors.grayscale['600']};

  /* Inline code styling */
  code {
    background-color: ${(props) =>
      props.$isUser ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
    padding: 2px 4px;
    border-radius: 3px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  }

  /* ReactMarkdown paragraph spacing */
  p {
    margin: 0 0 8px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
`

// (xdowns) Enable once we can install react-syntax-highlighter
// const CodeBlock = (props: {
//   inline?: boolean
//   className?: string
//   children?: React.ReactNode
// }) => {
//   const { inline, className, children } = props
//   const match = /language-(\w+)/.exec(className || '')
//   const language = match ? match[1] : 'text'

//   return !inline && match ? (
//     <SyntaxHighlighter
//       style={oneDark}
//       language={language}
//       PreTag='div'
//       customStyle={{
//         margin: '8px 0',
//         borderRadius: '4px',
//         fontSize: '13px',
//       }}>
//       {String(children).replace(/\n$/, '')}
//     </SyntaxHighlighter>
//   ) : (
//     <code className={className}>{children}</code>
//   )
// }

export const MessageBubble: FC<MessageBubbleProps> = ({
  message,
  showTimestamp,
  onCopy,
}) => {
  const isUser = message.role === 'user'

  return (
    <MessageRow $isUser={isUser}>
      <Avatar
        size='small'
        icon={isUser ? <UserOutlined /> : <RobotOutlined />}
        style={{
          backgroundColor: isUser ? '#1890ff' : CantileverTheme.colors.SeaGreen,
        }}
      />
      <MessageContent $isUser={isUser}>
        <MessageCard size='small' $isUser={isUser}>
          <MessageHeader>
            <Text
              strong
              style={{
                fontSize: '12px',
                color: isUser
                  ? 'rgba(255, 255, 255, 0.85)'
                  : CantileverTheme.colors.grayscale['600'],
              }}>
              {isUser ? 'You' : 'AI'}
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {showTimestamp && (
                <Text
                  type='secondary'
                  style={{
                    fontSize: '11px',
                    color: isUser
                      ? 'rgba(255, 255, 255, 0.65)'
                      : CantileverTheme.colors.grayscale['600'],
                  }}>
                  {message.timestamp}
                </Text>
              )}
              {!isUser && onCopy && (
                <CopyButton
                  size='small'
                  type='text'
                  icon={<CopyOutlined />}
                  onClick={() => onCopy(message.content)}
                  style={{ color: EXPLORER_HIGHLIGHT_COLOR }}
                />
              )}
            </div>
          </MessageHeader>
          <LlmMessageText $isUser={isUser}>
            <ReactMarkdown>
              {
                // (xdowns) Enable once we can install react-syntax-highlighter
                /*(components={{
                code: CodeBlock,
              }}>*/
              }
              {message.content}
            </ReactMarkdown>
          </LlmMessageText>
        </MessageCard>
      </MessageContent>
    </MessageRow>
  )
}
